import React from 'react';
import { type Theme, useStyletron } from 'baseui';
import { PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../../../constants/stage-css-variables';
import { CANVAS, HORIZONTAL_ANCHOR, VERTICAL_ANCHOR_TRANSLATE } from '../../../constants/template-builder';
import type { HorizontalAnchorT, VerticalAnchorT } from '../../../types/annotations.type';
import { getHorizontalAnchor, getScaledNodeOffset, getVerticalAnchor } from '../../../utils/node-position';
type NodeLabelPropsT = {
  x: number;
  y: number;
  label: string;
  highlighted: boolean;
  canvasDimensions: [number, number];
};
const getLabelStyles = ({
  theme,
  x,
  y,
  highlighted,
  horizontalAnchor,
  verticalAnchor
}: {
  theme: Theme;
  x: number;
  y: number;
  highlighted: boolean;
  horizontalAnchor: HorizontalAnchorT;
  verticalAnchor: VerticalAnchorT;
}) => ({
  position: ('absolute' as const),
  ...(horizontalAnchor === HORIZONTAL_ANCHOR.RIGHT ? {
    right: `calc(100% - ${x}px + ${getScaledNodeOffset()})`
  } : {
    left: `calc(${x}px + ${getScaledNodeOffset()})`
  }),
  top: `${y}px`,
  transform: `${VERTICAL_ANCHOR_TRANSLATE[verticalAnchor]} scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE}))`,
  transformOrigin: `${horizontalAnchor} ${verticalAnchor}`,
  ...theme.typography.LabelXSmall,
  color: highlighted ? theme.colors.contentPrimary : theme.colors.contentTertiary,
  whiteSpace: ('nowrap' as const),
  maxWidth: CANVAS.INPUT_WIDTH,
  overflow: ('hidden' as const),
  textOverflow: ('ellipsis' as const),
  pointerEvents: ('auto' as const)
});
export function NodeLabel({
  x,
  y,
  label,
  highlighted,
  canvasDimensions
}: NodeLabelPropsT) {
  const [css, theme] = useStyletron();
  const [canvasWidth, canvasHeight] = canvasDimensions;
  const horizontalAnchor = getHorizontalAnchor(x, canvasWidth);
  const verticalAnchor = getVerticalAnchor(y, canvasHeight);
  return <StatefulTooltip content={label} placement={PLACEMENT.top} showArrow>
      <div className={css(getLabelStyles({
      theme,
      x,
      y,
      highlighted,
      horizontalAnchor,
      verticalAnchor
    }))}>
        {label}
      </div>
    </StatefulTooltip>;
}